
'use client';

import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useAuth } from './auth-context';
import { useFirestore } from '@/firebase';
import { doc, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { useDebounce } from '@/hooks/use-debounce';
import { cartLinePrice, cartLineProductId, MAX_CART_ITEMS } from '@/lib/cart-line-id';
import { subscriptionAddConflict } from '@/lib/cart-subscriptions';
import { track } from '@/lib/analytics';
import { requestCartFlight } from '@/lib/cart-flight';
import { revealSiteHeader } from '@/lib/site-header-autohide';
import {
  browserLocalStorage,
  readStorageItem,
  removeStorageItems,
  writeStorageItem,
} from '@/lib/browser-storage';

// Narrower shape than the canonical `Product` in product-context.tsx — the cart
// only persists what the line-item UI needs (id, name, price, image URL,
// subscription flag). Callers strip the full Product down before passing it in.
export interface CartLineItem {
  id: string;
  name: string;
  price: number;
  image: string;
  isSubscription?: boolean;
  subscriptionFrequency?: string;
  // Chosen tier of a tiered product. The line `id` is `<productId>-<tierId>` so
  // two tiers stay separate rows, but checkout sends this field rather than
  // re-parsing that id — it is what the server prices the line against.
  tierId?: string;
}

interface CartContextType {
  cart: CartLineItem[];
  isLoaded: boolean;
  /**
   * `from` is the button that was pressed, for the add-to-cart shooting star
   * (src/lib/cart-flight.ts). Pass the click's `currentTarget`; it is measured
   * during the call, so it only has to be valid for the length of the click.
   */
  addToCart: (product: CartLineItem, options?: { silent?: boolean; from?: Element | null }) => void;
  removeFromCart: (productId: string) => void;
  clearCart: () => void;
  /**
   * Replace the whole cart in one step. For flows that decide the cart's
   * contents themselves (the `/buy` payment link) rather than adding a line to
   * whatever is there; see the implementation for why clear-then-add cannot
   * stand in for it.
   */
  setCartItems: (items: CartLineItem[]) => void;
  recipientEmail: string;
  setRecipientEmail: (email: string) => void;
}

/**
 * Drop any line superseded by a later line for the SAME product.
 *
 * Applied to both load paths, not just to addToCart, because the cart is
 * persisted with no expiry (Firestore when signed in, localStorage otherwise)
 * and carts holding two tiers of one product were writable until the guard in
 * addToCart landed. Those carts are still out there, and every one of them is
 * un-checkout-able: createCheckoutPaymentIntent refuses the whole cart when a
 * productId repeats. Repairing on load is what stops a buyer who built such a
 * cart last month from hitting a wall they cannot see the cause of.
 *
 * The LAST line wins, matching addToCart: the most recently chosen tier is the
 * one the buyer meant.
 */
function dropSupersededTiers(items: CartLineItem[]): CartLineItem[] {
  const lastIndexByProduct = new Map<string, number>();
  items.forEach((item, i) => {
    lastIndexByProduct.set(cartLineProductId(item.id, item.tierId), i);
  });
  return items.filter(
    (item, i) => lastIndexByProduct.get(cartLineProductId(item.id, item.tierId)) === i,
  );
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartLineItem[]>([]);
  const [recipientEmail, setRecipientEmail] = useState('');
  const { toast } = useToast();
  const { authUser, isUserLoading } = useAuth();
  const firestore = useFirestore();
  const [isLocalLoaded, setIsLocalLoaded] = useState(false);
  const [isFirestoreLoaded, setIsFirestoreLoaded] = useState(false);
  const authUserRef = useRef(authUser);

  const debouncedCart = useDebounce(cart, 1000);
  const debouncedEmail = useDebounce(recipientEmail, 1000);

  // Guards against the race condition where isFirestoreLoaded flips to true
  // before debouncedCart has resolved to the Firestore data, which would cause
  // the sync effect to write an empty cart and clear Firestore on every refresh.
  const isReadyToSyncRef = useRef(false);

  // Mirrors `cart` so addToCart can stay stable (no `cart` in its deps) and
  // still read a fresh value from its closure on rapid successive clicks.
  const cartRef = useRef(cart);
  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  // Same trick for the recipient address. Read by the sign-in merge below and by
  // the unload flush further down, both of which must see the current value
  // without taking `recipientEmail` as a dependency.
  const recipientEmailRef = useRef(recipientEmail);
  useEffect(() => { recipientEmailRef.current = recipientEmail; }, [recipientEmail]);

  // Load from localStorage on initial mount.
  // JSON.parse can return anything if the storage was tampered with, written
  // by an older app version with a different shape, or partially corrupted.
  // Without a shape check the downstream `cart.reduce(...)` math in checkout
  // can resolve to NaN and quietly break the page; validate here so we
  // discard garbage and start fresh.
  //
  // Every storage call in this provider goes through src/lib/browser-storage.ts.
  // With site data blocked, even reading `localStorage` throws, and this
  // provider wraps every route: one unguarded write in the save effect below
  // used to put the whole site on the global error screen. Blocked storage now
  // reads as an empty cart and the cart simply lives in memory.
  useEffect(() => {
    if (typeof window !== 'undefined') {
        const storage = browserLocalStorage();
        try {
            const storedCart = readStorageItem(storage, 'cart');
            if (storedCart) {
              const parsed: unknown = JSON.parse(storedCart);
              if (Array.isArray(parsed)) {
                const cleaned: CartLineItem[] = [];
                for (const raw of parsed) {
                  if (!raw || typeof raw !== 'object') continue;
                  const item = raw as Record<string, unknown>;
                  if (typeof item.id !== 'string' || item.id === '') continue;
                  if (typeof item.name !== 'string') continue;
                  // A tier price saved as text ("12.50") is still a price: this
                  // used to drop the line, so a guest's tier vanished on reload
                  // (src/lib/cart-line-id.ts, cartLinePrice).
                  const price = cartLinePrice(item.price);
                  if (price === null) continue;
                  if (typeof item.image !== 'string') continue;
                  // Optional fields are omitted, not set to undefined: this cart
                  // is mirrored straight into Firestore for signed-in buyers,
                  // and the SDK rejects a write containing undefined values.
                  cleaned.push({
                    id: item.id,
                    name: item.name,
                    price,
                    image: item.image,
                    ...(typeof item.isSubscription === 'boolean' ? { isSubscription: item.isSubscription } : {}),
                    ...(typeof item.subscriptionFrequency === 'string' ? { subscriptionFrequency: item.subscriptionFrequency } : {}),
                    ...(typeof item.tierId === 'string' ? { tierId: item.tierId } : {}),
                  });
                }
                if (cleaned.length > 0) setCart(dropSupersededTiers(cleaned));
                // If the stored value was malformed enough that nothing survived
                // validation, clear it so we don't keep retrying every mount.
                if (parsed.length > 0 && cleaned.length === 0) {
                  removeStorageItems(storage, 'cart');
                }
              } else {
                removeStorageItems(storage, 'cart');
              }
            }
            const storedEmail = readStorageItem(storage, 'recipientEmail');
            if (storedEmail) {
              const parsedEmail: unknown = JSON.parse(storedEmail);
              if (typeof parsedEmail === 'string') {
                setRecipientEmail(parsedEmail);
              } else {
                removeStorageItems(storage, 'recipientEmail');
              }
            }
        } catch (error) {
            console.error("Failed to parse cart from localStorage", error);
            // The stored blob is unrecoverable — drop it so the next mount
            // doesn't trip the same parse error in a loop.
            removeStorageItems(storage, 'cart', 'recipientEmail');
        } finally {
            setIsLocalLoaded(true);
        }
    }
  }, []);

  // Which uid's stored cart we have already adopted the local (guest) cart into.
  // The adoption must happen exactly ONCE per sign-in: on every later snapshot
  // the stored document is authoritative, and re-merging would resurrect a line
  // the user had just removed in another tab.
  const adoptedForUidRef = useRef<string | null>(null);

  // The guest copy has been folded into the signed-in cart, so drop it.
  //
  // THE MERGE IS NOT SAFE WITHOUT THIS. `adoptedForUidRef` is a ref, so "first
  // adoption" means the first snapshot of this PAGE LOAD, not the first
  // sign-in — and the localStorage copy is only ever written while signed out
  // (see the save effect below) and only ever cleared on logout or clearCart().
  // So it sits there, frozen at whatever the guest cart was, for the whole life
  // of the session, and every reload merged it in again:
  //
  //   guest adds A and B -> signs in (adopted, correct) -> removes B
  //   -> reloads -> stored [A] + stale local [A,B] -> B is back, and written
  //   back to Firestore. Repeats on every reload until logout or checkout.
  //
  // Clearing it here makes the merge a genuine one-time hand-off: every later
  // load starts from an empty local cart, so the merge branch becomes a no-op
  // and the stored document is authoritative again. Deliberately NOT called
  // when the adoption write fails — keeping the local copy is what lets the
  // next load retry the hand-off instead of losing the lines outright.
  const dropAdoptedLocalCart = useCallback(() => {
    // Best-effort: with sealed storage the merge already happened in memory.
    removeStorageItems(browserLocalStorage(), 'cart', 'recipientEmail');
  }, []);

  // Sync with Firestore when logged in
  useEffect(() => {
    if (!firestore || !authUser || !isLocalLoaded) return;

    setIsFirestoreLoaded(false);
    const uid = authUser.uid;
    if (adoptedForUidRef.current !== uid) adoptedForUidRef.current = null;

    const cartDocRef = doc(firestore, 'users', uid, 'cart', 'current_cart');
    const unsubscribe = onSnapshot(
      cartDocRef,
      (docSnap) => {
        // FIRST snapshot after sign-in MERGES; every one after it replaces.
        //
        // This used to replace unconditionally, and the `docSnap.exists()` guard
        // was doing all the work of hiding it: a brand-new account has no cart
        // document, so the guest cart survived and the bug never showed up in
        // testing. But clearCart() and every completed checkout write
        // `{ items: [] }`, so for any RETURNING buyer the document exists and is
        // empty — and signing in to pay for a cart built as a guest emptied it,
        // silently, at the last step of the funnel. Logging out then clears the
        // localStorage copy, so there was nothing left to recover from either.
        const isFirstAdoption = adoptedForUidRef.current !== uid;
        adoptedForUidRef.current = uid;

        const stored: CartLineItem[] = docSnap.exists() ? (docSnap.data().items || []) : [];
        const storedEmail = docSnap.exists() ? docSnap.data().recipientEmail : undefined;

        if (isFirstAdoption) {
          const local = cartRef.current;
          // Stored lines first, local additions after: dropSupersededTiers keeps
          // the LAST line for a product, so a tier chosen while signed out wins
          // over one sitting in the stored cart. That matches addToCart, where
          // adding a second tier is a change of selection rather than an error.
          const deduped = dropSupersededTiers([
            ...stored,
            ...local.filter(l => !stored.some(s => s.id === l.id)),
          ]);
          const merged = deduped.slice(0, MAX_CART_ITEMS);
          setCart(merged);
          // Say so when the ceiling ate something. addToCart tells the buyer
          // when a click is refused for being the eleventh item; a merge that
          // silently drops the products they picked as a guest is the same
          // event with no explanation, at the point they are least able to
          // guess what happened.
          if (deduped.length > merged.length) {
            toast({
              title: 'Cart full',
              description: `Your saved cart already had items, so ${deduped.length - merged.length === 1 ? 'one product' : `${deduped.length - merged.length} products`} you added before signing in couldn't be carried over. You can only have up to ${MAX_CART_ITEMS} items at once.`,
              variant: 'destructive',
            });
          }
          // Keep whichever address the buyer actually typed. The stored one
          // wins when set, since it is the one they confirmed while signed in.
          const localEmail = recipientEmailRef.current;
          if (storedEmail) setRecipientEmail(storedEmail);
          else if (!localEmail && storedEmail !== undefined) setRecipientEmail('');
          // Carried over from the guest session, so it has to go into the
          // adoption write below rather than wait for the debounced sync. We are
          // about to delete the localStorage copy, and until this reaches
          // Firestore that copy is the only record of it.
          const adoptedEmail = !storedEmail && localEmail ? localEmail : null;

          // Persist the adopted lines immediately rather than leaning on the
          // debounced sync effect. That effect is gated behind isReadyToSyncRef,
          // which only flips ~1100ms after this snapshot lands — by which point
          // the merged cart's own debounce has already fired and been skipped,
          // and nothing re-runs the effect (the gate is a ref, so flipping it
          // causes no re-render). The cart would sit in memory, correct on
          // screen, and reach Firestore only on the next mutation or the unload
          // flush. Writing here closes that window.
          //
          // No loop: this write comes back through the same listener, but
          // adoption has already been recorded so the next snapshot takes the
          // replace branch and finds nothing to change.
          const changed =
            merged.length !== stored.length ||
            merged.some((m, i) => m.id !== stored[i]?.id);
          if (changed || adoptedEmail) {
            setDoc(
              cartDocRef,
              { items: merged, ...(adoptedEmail ? { recipientEmail: adoptedEmail } : {}) },
              { merge: true },
            )
              .then(dropAdoptedLocalCart)
              .catch(err => console.error('Error adopting guest cart:', err));
          } else {
            dropAdoptedLocalCart();
          }
        } else if (docSnap.exists()) {
          setCart(dropSupersededTiers(stored));
          if (storedEmail !== undefined) {
            setRecipientEmail(storedEmail || '');
          }
        }
        setIsFirestoreLoaded(true);
      },
      () => {
        setIsFirestoreLoaded(true);
      }
    );

    return () => unsubscribe();
    // dropAdoptedLocalCart and toast are stable, but listing them keeps the
    // exhaustive-deps rule honest. A re-subscribe is harmless either way:
    // adoptedForUidRef has already recorded the adoption, so the next snapshot
    // takes the replace branch.
  }, [firestore, authUser, isLocalLoaded, dropAdoptedLocalCart, toast]);

  // Allow Firestore sync only after both auth + Firestore have loaded AND the
  // debounce has had time to settle (1000 ms).  Without this delay the sync
  // effect fires with debouncedCart still at [] (its initial value) and
  // overwrites the real Firestore cart with an empty array on every page refresh.
  useEffect(() => {
    if (!authUser || !isFirestoreLoaded) {
      isReadyToSyncRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      isReadyToSyncRef.current = true;
    }, 1100); // slightly longer than the 1000 ms debounce
    return () => clearTimeout(timer);
  }, [authUser, isFirestoreLoaded]);

  // Sync state changes to Firestore — intentionally does NOT include
  // isFirestoreLoaded so it only re-runs when the debounced values change,
  // not when Firestore first loads (which would fire with stale empty data).
  useEffect(() => {
    if (!firestore || !authUser || !isLocalLoaded || !isReadyToSyncRef.current) return;

    const cartRef = doc(firestore, 'users', authUser.uid, 'cart', 'current_cart');
    setDoc(
      cartRef,
      { items: debouncedCart, recipientEmail: debouncedEmail },
      { merge: true }
    ).catch(err => console.error("Error updating cart doc:", err));

  }, [debouncedCart, debouncedEmail, firestore, authUser, isLocalLoaded]);

  // Best-effort flush on tab unload so a refresh within the 1 s debounce
  // window doesn't drop the most recent cart edit. The Firestore SDK's
  // request is fire-and-forget here — most modern browsers keep the write
  // alive long enough to complete because the payload is small. If it
  // doesn't make it, the next mount falls back to whatever the listener
  // returns, same as before this guard existed.
  useEffect(() => {
    if (!firestore || !authUser || !isReadyToSyncRef.current) return;
    const flush = () => {
      if (!isReadyToSyncRef.current) return;
      try {
        const cartDocRef = doc(firestore, 'users', authUser.uid, 'cart', 'current_cart');
        setDoc(
          cartDocRef,
          { items: cartRef.current, recipientEmail: recipientEmailRef.current },
          { merge: true }
        ).catch(() => {});
      } catch { /* unload context — best-effort only */ }
    };
    window.addEventListener('beforeunload', flush);
    // pagehide fires on iOS Safari where beforeunload often does not.
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
    };
  }, [firestore, authUser]);

  // Handle logout
  useEffect(() => {
    if (!isUserLoading && authUserRef.current && !authUser) {
      setCart([]);
      setRecipientEmail('');
      setIsFirestoreLoaded(false);
      removeStorageItems(browserLocalStorage(), 'cart', 'recipientEmail');
    }
    authUserRef.current = authUser;
  }, [authUser, isUserLoading]);

  // Save to localStorage when not logged in. Best-effort: where storage is
  // blocked or full the guest cart lasts as long as the tab, rather than the
  // write throwing out of this effect and taking the page with it.
  useEffect(() => {
      if (!authUser && isLocalLoaded) {
          const storage = browserLocalStorage();
          writeStorageItem(storage, 'cart', JSON.stringify(cart));
          writeStorageItem(storage, 'recipientEmail', JSON.stringify(recipientEmail));
      }
  }, [cart, recipientEmail, authUser, isLocalLoaded]);

  const addToCart = useCallback((incoming: CartLineItem, options: { silent?: boolean; from?: Element | null } = {}) => {
    // Always a number in the cart. A tier's price can arrive as the text the
    // editor stored ("12.50"); kept as text it was dropped on the guest cart's
    // next reload. The line price is only shown: checkout prices from the product.
    const product: CartLineItem = { ...incoming, price: cartLinePrice(incoming.price) ?? 0 };
    // Fast path drives the toast UX from the latest committed cart via the ref.
    const current = cartRef.current;
    if (current.some(item => item.id === product.id)) {
      if (!options.silent) toast({ title: "Already in your cart", description: product.name });
      return;
    }

    // ONE SUBSCRIPTION, AND NOTHING BESIDE IT. Also the server's rule:
    // createCheckoutPaymentIntent refuses a cart holding a subscription and
    // anything else, all of it, so letting the line in here only moved the
    // refusal to the payment step. See src/lib/cart-subscriptions.ts, which
    // also covers why a tier change of the same product is not a conflict.
    // Toasted even for a silent add, like the full cart below: the line is
    // refused, and a click that changes nothing has to say why.
    const conflict = subscriptionAddConflict(current, product);
    if (conflict) {
      toast({
        title: "Subscriptions check out on their own",
        description: conflict.held
          ? `Your cart has a subscription, ${conflict.held.name}, and a subscription has to be bought by itself. Complete that checkout or remove it first, then add ${product.name}.`
          : `${product.name} is a subscription, so it has to be bought by itself. Check out or remove what is already in your cart first, then subscribe.`,
        variant: "destructive",
      });
      return;
    }

    // ONE TIER PER PRODUCT, and the server is the reason. A tiered line's id is
    // `<productId>-<tierId>`, deliberately, so two tiers of one product are two
    // distinct rows. The check above dedupes on that whole id, so it lets both
    // rows in. createCheckoutPaymentIntent then refuses the cart outright,
    // "Your cart has the same product twice. Pick one tier per product and try
    // again.", and it refuses ALL of it, not just the offending line.
    //
    // So the buyer could assemble a cart, see a total, and only discover at the
    // payment step that it could never have been paid for, with nothing on the
    // cart page pointing at which of the ten rows to remove. Nothing between the
    // product page and the intent guarded it.
    //
    // Adding a second tier is treated as CHANGING the selection rather than
    // refused, because that is what the click means: a buyer already holding
    // Tier A who adds Tier B wants Tier B. Entitlement is one tier per product
    // anyway, so there is no reading under which they wanted both.
    const productId = cartLineProductId(product.id, product.tierId);
    const supersedes = current.find(
      item => item.id !== product.id && cartLineProductId(item.id, item.tierId) === productId,
    );
    if (supersedes) {
      if (!options.silent) {
        toast({
          title: "Tier updated",
          description: `Your cart now has ${product.name} instead of ${supersedes.name}.`,
        });
      }
      setCart(prevCart => prevCart.map(item => (item.id === supersedes.id ? product : item)));
      return;
    }

    if (current.length >= MAX_CART_ITEMS) {
      toast({ title: "Cart full", description: `You can only have up to ${MAX_CART_ITEMS} items in your cart at once.`, variant: "destructive" });
      return;
    }
    // A REAL ADD, and the only branch that launches the shooting star: an item
    // already in the cart, a tier swap and a full cart each return above. When
    // the star flies it IS the confirmation, so the toast is skipped (on a
    // phone it would slide over the very cart the star lands on) and the add is
    // announced through the flight layer's polite live region instead. With the
    // effect off, or no button to fly from, this is the toast it always was.
    //
    // Asked here, synchronously, before setCart: the store measures the button
    // now (a product card's button is replaced by the re-render this add
    // causes), and holding a first item's dot has to land in the same render
    // as the item, or the dot flashes for a frame before hiding.
    const flying = requestCartFlight({
      key: product.id,
      from: options.from,
      wasEmpty: current.length === 0,
      announce: options.silent ? undefined : `Added ${product.name} to cart`,
    });
    if (!flying && !options.silent) toast({ title: "Added to your cart", description: product.name });
    // On the marketplace the site header may have scrolled away; bring it
    // back, so the cart count changing (and the star landing) is on screen. A
    // no-op on every other page (src/lib/site-header-autohide.ts).
    revealSiteHeader();
    // Analytics funnel, step 2. A no-op until the visitor accepts analytics cookies.
    track('cart.add', { productId, price: product.price });

    // Re-check inside the updater so two clicks queued before React commits
    // can't both pass the closure checks above and cause a duplicate, a second
    // tier of the same product, a subscription sharing the cart, or an overflow.
    setCart(prevCart => {
      if (prevCart.some(item => item.id === product.id)) return prevCart;
      if (subscriptionAddConflict(prevCart, product)) return prevCart;
      const clash = prevCart.find(
        item => cartLineProductId(item.id, item.tierId) === productId,
      );
      if (clash) return prevCart.map(item => (item.id === clash.id ? product : item));
      if (prevCart.length >= MAX_CART_ITEMS) return prevCart;
      return [...prevCart, product];
    });
  }, [toast]);

  const removeFromCart = useCallback((productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
    track('cart.remove', { productId });
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    setRecipientEmail('');
    removeStorageItems(browserLocalStorage(), 'cart', 'recipientEmail');
  }, []);

  // Replace the cart wholesale, as one change.
  //
  // WHY NOT clearCart() + addToCart(). addToCart reads cartRef.current, and that
  // ref is only brought up to date by an effect AFTER React commits. Called in
  // the same tick as clearCart() (or removeFromCart()), it still sees the old
  // lines: it can report "Already in your cart" and do nothing, or swap a tier inside
  // a cart that was meant to be empty. clearCart() also wipes the recipient
  // email, which a buyer following a payment link has not asked for. This
  // method writes the ref synchronously, so a following addToCart in the same
  // tick sees exactly the cart set here.
  //
  // WHY THE IMMEDIATE FIRESTORE WRITE. The signed-in cart normally reaches
  // Firestore through the debounced sync effect above, and that effect is gated
  // on isReadyToSyncRef, which only flips ~1100ms after the cart document first
  // loads. A change made inside that window is skipped: the debounce fires, the
  // gate is still closed, and because the gate is a ref nothing re-runs the
  // effect when it opens. The `/buy` page is precisely the caller that writes in
  // that window, because it sets the cart the moment the cart has loaded. The
  // buyer would then see the right line at checkout while Firestore still held
  // the old cart, and the next snapshot (another tab, a reload after a failed
  // card) would put the old lines back. Writing here closes that window, the
  // same way the guest-cart adoption write in the listener closes it.
  //
  // Guests need nothing extra: the localStorage save effect runs on every cart
  // change. `recipientEmail` is left alone in both cases (merge: true, and the
  // field is not in the payload).
  const setCartItems = useCallback((items: CartLineItem[]) => {
    // Same normalisation as the load paths and addToCart: the server refuses a
    // cart that names one product twice, and the cart holds MAX_CART_ITEMS lines.
    const next = dropSupersededTiers(items).slice(0, MAX_CART_ITEMS);
    cartRef.current = next;
    setCart(next);

    if (authUser && firestore && isFirestoreLoaded) {
      const cartDocRef = doc(firestore, 'users', authUser.uid, 'cart', 'current_cart');
      setDoc(cartDocRef, { items: next }, { merge: true })
        .catch(err => console.error('Error writing cart doc:', err));
    }
  }, [authUser, firestore, isFirestoreLoaded]);

  const isLoaded = isLocalLoaded && !isUserLoading && (!authUser || isFirestoreLoaded);

  const value = { cart, isLoaded, addToCart, removeFromCart, clearCart, setCartItems, recipientEmail, setRecipientEmail };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
